import React, { useState, useEffect, useMemo } from 'react';
import {
  Select,
  Input,
  Button,
  Card,
  Row,
  Col,
  Badge,
  Empty,
  Tooltip,
  message,
  Spin,
  Modal,
  Form,
  Upload,
  Checkbox
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  EditOutlined,
  DeleteOutlined,
  FilterOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  DatabaseOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileOutlined,
  UploadOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined
} from '@ant-design/icons';
import { FileDto, DocumentType, DOCUMENT_TYPE_LABELS, getDocumentTypeOptions, FileType } from '../../../types/file.types';
import { User } from '../../../types/user.types';
import FileService from '../../../services/file/file.service';
import UserService from '../../../services/user/user.service';
import { formatFileSize } from '../../../utils/file.utils';
import dayjs from 'dayjs';

const { Dragger } = Upload;

const DocumentsPage: React.FC = () => {
  const [documents, setDocuments] = useState<FileDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | 'all'>('all');
  const [selectedUser, setSelectedUser] = useState<number | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Kişiye özel dosya yükleme state'leri
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [employees, setEmployees] = useState<User[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [uploadForm] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);

  // Düzenleme modal state'leri
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingDocument, setEditingDocument] = useState<FileDto | null>(null);
  const [editForm] = Form.useForm();
  const [editFileList, setEditFileList] = useState<any[]>([]);
  const [updating, setUpdating] = useState(false);

  // Önizleme modal state'leri
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileDto | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const fileService = FileService.getInstance();
  const userService = UserService;

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await FileService.getInstance().getFiles();
      const documentsData = (response as any)?.data || response || [];
      const documents = documentsData.filter((doc: FileDto) => doc.type === FileType.DOCUMENT);
      setDocuments(Array.isArray(documents) ? documents : []);
    } catch (error) {
      message.error('Belgeler yüklenirken hata oluştu');
      setDocuments([]); // Hata durumunda boş array
    } finally {
      setLoading(false);
    }
  };

  // Çalışanları getir
  const fetchEmployees = React.useCallback(async () => {
    try {
      const response = await userService.getUsers();
      setEmployees(response || []);
    } catch (error) {
      message.error('Çalışanlar yüklenirken hata oluştu');
    }
  }, [userService]);

  // Belgeleri yükle
  useEffect(() => {
    fetchDocuments();
    fetchEmployees();
  }, [fetchEmployees]);

  // Dosya ikonu belirleme
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FilePdfOutlined className="text-danger fs-2" />;
      case 'doc':
      case 'docx':
        return <FileWordOutlined className="text-primary fs-2" />;
      case 'xls':
      case 'xlsx':
        return <FileExcelOutlined className="text-success fs-2" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileImageOutlined className="text-warning fs-2" />;
      default:
        return <FileOutlined className="text-secondary fs-2" />;
    }
  };

  // Belge tipi badge rengini belirleme
  const getDocumentTypeBadgeColor = (type: DocumentType) => {
    const colors: Record<DocumentType, string> = {
      [DocumentType.ID_CARD]: 'processing',
      [DocumentType.ID_PHOTO]: 'success',
      [DocumentType.PASSPORT]: 'purple',
      [DocumentType.DRIVER_LICENSE]: 'cyan',
      [DocumentType.DIPLOMA]: 'gold',
      [DocumentType.CERTIFICATE]: 'lime',
      [DocumentType.HEALTH_REPORT]: 'red',
      [DocumentType.CRIMINAL_RECORD]: 'orange',
      [DocumentType.RESIDENCE]: 'blue',
      [DocumentType.SGK_ENTRY_DOCUMENT]: 'green',
      [DocumentType.EMPLOYMENT_CONTRACT]: 'yellow',
      [DocumentType.MILITARY_SERVICE_CERTIFICATE]: 'pink'
    };
    return colors[type] || 'default';
  };

  // Filtrelenmiş belgeler
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(doc =>
        doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (doc.user?.firstName + ' ' + doc.user?.lastName).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Belge tipi filtresi
    if (selectedDocumentType !== 'all') {
      filtered = filtered.filter(doc => doc.documentType === selectedDocumentType);
    }

    // Kullanıcı filtresi
    if (selectedUser !== 'all') {
      filtered = filtered.filter(doc => doc.user?.id === selectedUser);
    }

    return filtered;
  }, [documents, searchTerm, selectedDocumentType, selectedUser]);

  // Benzersiz kullanıcıları al
  const uniqueUsers = useMemo(() => {
    const users = documents
      .filter(doc => doc.user)
      .map(doc => doc.user!)
      .filter((user, index, self) =>
        index === self.findIndex(u => u.id === user.id)
      );
    return users;
  }, [documents]);



  // Dosya indirme
  const handleDownload = async (doc: FileDto) => {
    try {
      const fileName = doc.name;
      const blob = await fileService.getFileViewUrl(doc.path);
      
      // Blob boşsa veya hatalıysa
      if (!blob || blob.size === 0) {
        message.error('Dosya bulunamadı');
        return;
      }
      
      // Blob'dan Object URL oluştur
      const url = window.URL.createObjectURL(blob);
      
      // Link oluştur ve indir
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Object URL'i temizle
      window.URL.revokeObjectURL(url);
      
      message.success('Dosya indiriliyor');
    } catch (error: any) {
      if (error?.response?.status === 404) {
        message.error('Dosya bulunamadı');
      } else if (error?.message?.includes('404')) {
        message.error('Dosya bulunamadı');
      } else {
        message.error('Dosya indirilirken hata oluştu');
      }
    }
  };

  // Dosya görüntüleme (Modal ile)
  const handleView = async (doc: FileDto) => {
    try {
      const blob = await fileService.getFileViewUrl(doc.path);
      
      // Blob boşsa veya hatalıysa
      if (!blob || blob.size === 0) {
        message.error('Dosya bulunamadı');
        return;
      }
      
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewFile(doc);
      setPreviewVisible(true);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        message.error('Dosya bulunamadı');
      } else if (error?.message?.includes('404')) {
        message.error('Dosya bulunamadı');
      } else {
        message.error('Dosya görüntülenirken hata oluştu');
      }
    }
  };

  // Önizleme modalını kapat
  const handleClosePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    setPreviewVisible(false);
    setPreviewFile(null);
  };

  // Dosyanın görsel olup olmadığını kontrol et
  const isImageFile = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '');
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDocumentType('all');
    setSelectedUser('all');
  };

  // Kişiye özel dosya yükleme modal'ını aç
  const openUploadModal = () => {
    setIsUploadModalVisible(true);
    uploadForm.resetFields();
    setFileList([]);
    setSelectedEmployee(null);
  };

  // Modal'ı kapat
  const closeUploadModal = () => {
    setIsUploadModalVisible(false);
    uploadForm.resetFields();
    setFileList([]);
    setSelectedEmployee(null);
  };

  // Dosya yükleme işlemi
  const handleUploadFile = async (values: any) => {
    if (!selectedEmployee) {
      message.error('Lütfen bir çalışan seçin');
      return;
    }

    if (fileList.length === 0) {
      message.error('Lütfen bir dosya seçin');
      return;
    }

    try {
      setUploading(true);
      const file = fileList[0].originFileObj || fileList[0];

      await fileService.uploadFileForEmployee(
        file,
        selectedEmployee,
        values.fileName,
        values.documentType,
        values.isVisible !== false
      );

      message.success('Dosya başarıyla yüklendi');
      closeUploadModal();
      await fetchDocuments(); // Dosya listesini yenile
    } catch (error) {
      message.error('Dosya yüklenirken hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  // Upload component props
  const uploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    beforeUpload: (file: File) => {
      setFileList([file]);
      return false; // Otomatik yüklemeyi engelle
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  // Edit upload component props
  const editUploadProps = {
    name: 'file',
    multiple: false,
    fileList: editFileList,
    beforeUpload: (file: File) => {
      setEditFileList([file]);
      return false; // Otomatik yüklemeyi engelle
    },
    onRemove: () => {
      setEditFileList([]);
    },
  };

  // Düzenleme modal'ını aç
  const openEditModal = (document: FileDto) => {
    setEditingDocument(document);
    setIsEditModalVisible(true);
    editForm.setFieldsValue({
      fileName: document.name,
      documentType: document.documentType,
      isVisible: document.isVisible !== false
    });
    setEditFileList([]);
  };

  // Düzenleme modal'ını kapat
  const closeEditModal = () => {
    setIsEditModalVisible(false);
    setEditingDocument(null);
    editForm.resetFields();
    setEditFileList([]);
  };

  // Belge düzenleme işlemi
  const handleEditDocument = async (values: any) => {
    if (!editingDocument) {
      message.error('Düzenlenecek belge bulunamadı');
      return;
    }

    try {
      setUpdating(true);
      const file = editFileList.length > 0 ? (editFileList[0].originFileObj || editFileList[0]) : undefined;

      await fileService.updateFileForEmployee(
        editingDocument.id,
        values.fileName,
        values.documentType,
        file
      );

      message.success('Belge başarıyla güncellendi');
      closeEditModal();
      await fetchDocuments(); // Belge listesini yenile
    } catch (error) {
      message.error('Belge güncellenirken hata oluştu');
    } finally {
      setUpdating(false);
    }
  };

  // Belge silme onayı
  const confirmDeleteDocument = (document: FileDto) => {
    Modal.confirm({
      title: 'Belgeyi Sil',
      icon: <ExclamationCircleOutlined />,
      content: `"${document.name}" belgesi kalıcı olarak silinecek. Bu işlem geri alınamaz. Devam etmek istiyor musunuz?`,
      okText: 'Evet, Sil',
      okType: 'danger',
      cancelText: 'İptal',
      onOk: () => handleDeleteDocument(document),
    });
  };

  // Belge silme işlemi
  const handleDeleteDocument = async (document: FileDto) => {
    try {
      await fileService.deleteFile(document.id);
      message.success('Belge başarıyla silindi');
      await fetchDocuments(); // Belge listesini yenile
    } catch (error) {
      message.error('Belge silinirken hata oluştu');
    }
  };

  // Görünürlük durumunu değiştir
  const toggleVisibility = async (document: FileDto) => {
    try {
      const newVisibility = !document.isVisible;
      await fileService.toggleFileVisibility(document.id, newVisibility);
      message.success(newVisibility ? 'Belge görünür yapıldı' : 'Belge gizlendi');
      await fetchDocuments(); // Belge listesini yenile
    } catch (error) {
      message.error('Görünürlük durumu değiştirilirken hata oluştu');
    }
  };

  // Belge kartı bileşeni
  const DocumentCard = ({ document }: { document: FileDto }) => (
    <Card
      className="document-card h-100 shadow-sm hover-shadow-lg"
      bodyStyle={{ padding: '16px' }}
      actions={[
        <Tooltip title="Görüntüle">
          <Button
            type="text"
            icon={<FileSearchOutlined />}
            onClick={() => handleView(document)}
          />
        </Tooltip>,
        <Tooltip title="İndir">
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(document)}
          />
        </Tooltip>,
        <Tooltip title={document.isVisible ? "Gizle" : "Görünür Yap"}>
          <Button
            type="text"
            icon={document.isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={() => toggleVisibility(document)}
            danger={!document.isVisible}
          />
        </Tooltip>,
        <Tooltip title="Düzenle">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(document)}
          />
        </Tooltip>,
        <Tooltip title="Sil">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => confirmDeleteDocument(document)}
          />
        </Tooltip>
      ]}
    >
      <div className="d-flex flex-column h-100">
        {/* Badge'ler */}
        <div className="mb-2 d-flex flex-wrap gap-1">
          {document.documentType && (
            <Badge
              color={getDocumentTypeBadgeColor(document.documentType)}
              text={DOCUMENT_TYPE_LABELS[document.documentType]}
              size="small"
            />
          )}
          {document.isVisible === false && (
            <Badge
              color="orange"
              text="Gizli"
              size="small"
            />
          )}
        </div>

        {/* Dosya ikonu ve ismi */}
        <div className="text-center mb-3">
          {getFileIcon(document.name)}
          <h6 className="mt-2 mb-0 text-truncate" title={document.name}>
            {document.name}
          </h6>
        </div>

        {/* Kullanıcı bilgisi */}
        <div className="document-info mt-auto">
          <div className="d-flex align-items-center mb-2">
            <UserOutlined className="me-2 text-muted" />
            <span className="small text-muted">
              {document.user ? `${document.user.firstName} ${document.user.lastName}` : 'Bilinmeyen'}
            </span>
          </div>

          <div className="d-flex align-items-center mb-2">
            <CalendarOutlined className="me-2 text-muted" />
            <span className="small text-muted">
              {dayjs(document.createdAt).format('DD.MM.YYYY')}
            </span>
          </div>

          <div className="d-flex align-items-center">
            <DatabaseOutlined className="me-2 text-muted" />
            <span className="small text-muted">
              {formatFileSize(document.size)}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );

  // Liste görünümü bileşeni
  const DocumentListItem = ({ document }: { document: FileDto }) => (
    <Card className="document-list-item mb-3 shadow-sm">
      <Row align="middle" gutter={[16, 16]}>
        <Col xs={24} sm={8} md={6} lg={4}>
          <div className="d-flex align-items-center">
            {getFileIcon(document.name)}
            <div className="ms-3">
              <h6 className="mb-0 text-truncate" title={document.name}>
                {document.name}
              </h6>
              <div className="d-flex flex-wrap gap-1">
                {document.documentType && (
                  <Badge
                    color={getDocumentTypeBadgeColor(document.documentType)}
                    text={DOCUMENT_TYPE_LABELS[document.documentType]}
                    size="small"
                  />
                )}
                {document.isVisible === false && (
                  <Badge
                    color="orange"
                    text="Gizli"
                    size="small"
                  />
                )}
              </div>
            </div>
          </div>
        </Col>

        <Col xs={24} sm={6} md={5} lg={4}>
          <div className="d-flex align-items-center">
            <UserOutlined className="me-2 text-muted" />
            <span className="small">
              {document.user ? `${document.user.firstName} ${document.user.lastName}` : 'Bilinmeyen'}
            </span>
          </div>
        </Col>

        <Col xs={12} sm={5} md={4} lg={3}>
          <div className="d-flex align-items-center">
            <CalendarOutlined className="me-2 text-muted" />
            <span className="small">
              {dayjs(document.createdAt).format('DD.MM.YYYY')}
            </span>
          </div>
        </Col>

        <Col xs={12} sm={5} md={4} lg={3}>
          <div className="d-flex align-items-center">
            <DatabaseOutlined className="me-2 text-muted" />
            <span className="small">
              {formatFileSize(document.size)}
            </span>
          </div>
        </Col>

        <Col xs={24} sm={24} md={8} lg={8}>
          <div className="d-flex justify-content-end gap-1">
            <Tooltip title="Görüntüle">
              <Button
                type="text"
                size="small"
                icon={<FileSearchOutlined />}
                onClick={() => handleView(document)}
              />
            </Tooltip>
            <Tooltip title="İndir">
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(document)}
              />
            </Tooltip>
            <Tooltip title={document.isVisible ? "Gizle" : "Görünür Yap"}>
              <Button
                type="text"
                size="small"
                icon={document.isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                onClick={() => toggleVisibility(document)}
                danger={!document.isVisible}
              />
            </Tooltip>
            <Tooltip title="Düzenle">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditModal(document)}
              />
            </Tooltip>
            <Tooltip title="Sil">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => confirmDeleteDocument(document)}
              />
            </Tooltip>
          </div>
        </Col>
      </Row>
    </Card>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Sayfa başlığı */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-sm-12">
              <div className="page-sub-header">
                <h3 className="page-title">
                  <FileTextOutlined className="me-2" />
                  Belgeler
                </h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="index.html">Raporlar</a>
                  </li>
                  <li className="breadcrumb-item active">Belgeler</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* İstatistikler */}
        {/* <Row gutter={[16, 16]} className="mb-4">
          <Col xs={12} sm={6} md={6} lg={6}>
            <Card>
              <Statistic
                title="Toplam Belge"
                value={statistics.totalDocuments}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6}>
            <Card>
              <Statistic
                title="Kullanıcı Sayısı"
                value={statistics.uniqueUsersCount}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6}>
            <Card>
              <Statistic
                title="Bu Hafta"
                value={statistics.recentDocuments}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6}>
            <Card>
              <Statistic
                title="Belge Türü"
                value={Object.keys(statistics.documentsByType).length}
                prefix={<FileOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row> */}

        {/* Filtreler */}
        <Card className="mb-4">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input
                placeholder="Belge veya kullanıcı ara..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Belge Türü"
                value={selectedDocumentType}
                onChange={setSelectedDocumentType}
                style={{ width: '100%' }}
                allowClear
              >
                <Select.Option value="all">Tüm Belgeler</Select.Option>
                {getDocumentTypeOptions().map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Kullanıcı Seç"
                value={selectedUser}
                onChange={setSelectedUser}
                style={{ width: '100%' }}
                allowClear
              >
                <Select.Option value="all">Tüm Kullanıcılar</Select.Option>
                {uniqueUsers.map(user => (
                  <Select.Option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={24} md={12} lg={6}>
              <div className="d-flex flex-column gap-2">
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    onClick={clearFilters}
                    icon={<FilterOutlined />}
                  >
                    Temizle
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openUploadModal}
                    size="middle"
                  >
                    Ekle
                  </Button>
                </div>
                <Button.Group>
                  <Button
                    type={viewMode === 'grid' ? 'primary' : 'default'}
                    onClick={() => setViewMode('grid')}
                  >
                    Kart
                  </Button>
                  <Button
                    type={viewMode === 'list' ? 'primary' : 'default'}
                    onClick={() => setViewMode('list')}
                  >
                    Liste
                  </Button>
                </Button.Group>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Yeni Belge Yükle Butonu */}
        {/* <Card className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Belge İşlemleri</h5>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openUploadModal}
              size="large"
            >
              Kişiye Özel Belge Yükle
            </Button>
          </div>
        </Card> */}

        {/* Kişiye Özel Belge Yükleme Modal'ı */}
        <Modal
          title="Kişiye Özel Belge Yükle"
          open={isUploadModalVisible}
          onCancel={closeUploadModal}
          footer={null}
          width={800}
          destroyOnClose
        >
          <Form
            form={uploadForm}
            layout="vertical"
            onFinish={handleUploadFile}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  label="Çalışan Seç"
                  name="employeeId"
                  rules={[{ required: true, message: 'Lütfen bir çalışan seçin' }]}
                >
                  <Select
                    placeholder="Çalışan seçin..."
                    showSearch
                    optionFilterProp="children"
                    value={selectedEmployee}
                    onChange={setSelectedEmployee}
                    style={{ width: '100%' }}
                  >
                    {employees.map(employee => (
                      <Select.Option key={employee.id} value={employee.id}>
                        <div className="d-flex align-items-center">
                          <UserOutlined className="me-2" />
                          {employee.firstName} {employee.lastName} ({employee.email})
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label="Dosya Seç"
                  required
                >
                  <Dragger {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Dosyayı buraya sürükleyin veya tıklayarak seçin
                    </p>
                    <p className="ant-upload-hint">
                      Tek dosya yükleme desteklenir. PDF, DOC, DOCX, JPG, PNG formatları kabul edilir.
                    </p>
                  </Dragger>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Dosya Adı"
                  name="fileName"
                  rules={[{ required: true, message: 'Lütfen dosya adını girin' }]}
                >
                  <Input placeholder="Dosya adını girin..." />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Belge Türü"
                  name="documentType"
                  rules={[{ required: true, message: 'Lütfen belge türünü seçin' }]}
                >
                  <Select placeholder="Belge türü seçin...">
                    {getDocumentTypeOptions().map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="isVisible"
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Checkbox>
                    Çalışan tarafından görüntülenebilir
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col span={24}>
                <div className="d-flex justify-content-end gap-2">
                  <Button onClick={closeUploadModal}>
                    İptal
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={uploading}
                    icon={<UploadOutlined />}
                  >
                    Dosyayı Yükle
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* Belge Düzenleme Modal'ı */}
        <Modal
          title="Belge Düzenle"
          open={isEditModalVisible}
          onCancel={closeEditModal}
          footer={null}
          width={800}
          destroyOnClose
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleEditDocument}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div className="mb-3 p-3 bg-light rounded">
                  <strong>Düzenlenmekte olan belge:</strong> {editingDocument?.name}
                  {editingDocument?.user && (
                    <div className="text-muted small">
                      Çalışan: {editingDocument.user.firstName} {editingDocument.user.lastName}
                    </div>
                  )}
                </div>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Dosya Adı"
                  name="fileName"
                  rules={[{ required: true, message: 'Lütfen dosya adını girin' }]}
                >
                  <Input placeholder="Dosya adını girin..." />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Belge Türü"
                  name="documentType"
                  rules={[{ required: true, message: 'Lütfen belge türünü seçin' }]}
                >
                  <Select placeholder="Belge türü seçin...">
                    {getDocumentTypeOptions().map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label="Yeni Dosya (İsteğe Bağlı)"
                  help="Dosyayı değiştirmek istemiyorsanız boş bırakabilirsiniz"
                >
                  <Dragger {...editUploadProps}>
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Yeni dosyayı buraya sürükleyin veya tıklayarak seçin
                    </p>
                    <p className="ant-upload-hint">
                      Dosyayı değiştirmek istemiyorsanız bu alanı boş bırakabilirsiniz.
                    </p>
                  </Dragger>
                </Form.Item>
              </Col>

              <Col span={24}>
                {editingDocument?.isVisible ? (
                  <div className="d-flex align-items-center mb-2">
                      Çalışan tarafından görüntülenebilir
                  </div>
                ) : (
                  <div className="d-flex align-items-center mb-2">
                    Çalışan tarafından görüntülenemez
                  </div>
                )}
              </Col>

              <Col span={24}>
                <div className="d-flex justify-content-end gap-2">
                  <Button onClick={closeEditModal}>
                    İptal
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={updating}
                    icon={<EditOutlined />}
                  >
                    Belgeyi Güncelle
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* Belge listesi */}
        <Card>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              {filteredDocuments.length} belge bulundu
            </h5>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spin size="large" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Belge bulunamadı"
            />
          ) : (
            <>
              {viewMode === 'grid' ? (
                <Row gutter={[16, 16]}>
                  {filteredDocuments.map(document => (
                    <Col key={document.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                      <DocumentCard document={document} />
                    </Col>
                  ))}
                </Row>
              ) : (
                <div>
                  {filteredDocuments.map(document => (
                    <DocumentListItem key={document.id} document={document} />
                  ))}
                </div>
              )}
            </>
          )}
        </Card>

        {/* Önizleme Modal'ı */}
        <Modal
          open={previewVisible}
          onCancel={handleClosePreview}
          footer={[
            <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={() => previewFile && handleDownload(previewFile)}>
              İndir
            </Button>,
            <Button key="close" onClick={handleClosePreview}>
              Kapat
            </Button>
          ]}
          width={800}
          centered
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <EyeOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
              <span>{previewFile?.name || 'Dosya Önizleme'}</span>
            </div>
          }
        >
           {previewFile && previewUrl && (
             <div style={{ 
               display: 'flex', 
               justifyContent: 'center', 
               alignItems: 'center',
               width: '100%',
               minHeight: '400px',
               maxHeight: '70vh',
               background: '#f5f5f5',
               borderRadius: '8px',
               padding: '20px',
               overflow: 'auto'
             }}>
               {isImageFile(previewFile.path || previewFile.name) ? (
                 <img 
                   src={previewUrl} 
                   alt={previewFile.name} 
                   style={{ 
                     maxWidth: '100%',
                     maxHeight: '65vh',
                     height: 'auto',
                     width: 'auto',
                     objectFit: 'contain',
                     borderRadius: '4px',
                     display: 'block',
                     margin: '0 auto'
                   }} 
                 />
               ) : (
                 <iframe
                   src={previewUrl}
                   style={{
                     width: '100%',
                     height: '65vh',
                     border: 'none',
                     borderRadius: '4px'
                   }}
                   title={previewFile.name}
                 />
               )}
             </div>
           )}
        </Modal>
      </div>

      {/* Stil tanımlamaları */}
      <style>
        {`
          .document-card {
            transition: all 0.3s ease;
            border-radius: 8px;
          }
          .document-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
          }
          .document-list-item {
            transition: all 0.2s ease;
            border-radius: 8px;
          }
          .document-list-item:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
          }
          .hover-shadow-lg {
            transition: all 0.3s ease;
          }
          .hover-shadow-lg:hover {
            transform: translateY(-3px);
            box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.1) !important;
          }
          .document-info {
            font-size: 0.85rem;
          }
          .ant-statistic-content {
            font-size: 1.2rem;
          }
          .ant-card-actions {
            background: #fafafa;
          }
          .ant-card-actions > li {
            margin: 4px 0;
          }
          .ant-upload-drag .ant-upload-drag-icon {
            margin-bottom: 16px;
          }
          .ant-upload-drag .ant-upload-text {
            font-size: 16px;
            color: #666;
          }
          .ant-upload-drag .ant-upload-hint {
            color: #999;
          }
          .ant-badge {
            margin-right: 4px;
          }
          .document-card .ant-card-actions {
            border-top: 1px solid #f0f0f0;
          }
          .document-card .ant-card-actions > li {
            margin: 6px 0;
          }
          .document-card .ant-card-actions > li > span {
            padding: 0 4px;
          }
          .document-list-item .ant-btn {
            margin: 0 2px;
          }
          .bg-light {
            background-color: #f8f9fa !important;
          }
        `}
      </style>
    </div>
  );
};

export default DocumentsPage; 